import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { Counter } from "PFApp/components/counter";
import { SearchInput } from "PFApp/components/search_input";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { WORKFLOW_STATES } from "../../../constants/workflow_states";
import { getValueFilters } from "../../../use_filtered_collection/format_filters";
import { useActiveCounter } from "../use_active_counter";
import css from "./roles_header.module.scss";

const RolesHeader = ({ className }) => {
  const {
    store: {
      pages: {
        workflowPage: {
          roles: {
            data: { meta },
            counters: fetchedCounters,
            onFilterChange,
            onFiltersReset
          }
        }
      }
    }
  } = useContext(AppContext);
  const stringify = useDeterministicStringify();
  const { t } = useTranslation("workflow");

  const isEnabled = useIsFeatureEnabled();

  const valueFilters = getValueFilters(meta.filters);
  delete valueFilters.fields?.state;

  const termFilter = meta.filters.fields?.term;
  const handleTermChange = (term) => onFilterChange(termFilter, term);

  const statusFilters = (code) => {
    const counter = counters.find((item) => item.code === code);
    return counter.filters;
  };

  const termValue = termFilter?.value || "";

  const isBookingModuleEnabled = isEnabled(FeatureFlag.BookingModule);

  const counters = useMemo(
    () =>
      [
        {
          name: t("parts.counters.totalOpen"),
          code: "new",
          separator: true,
          filters: {
            fields: {
              state: ["new"],
              workflow_state: Object.keys(WORKFLOW_STATES)
            }
          }
        },
        {
          name: t("parts.counters.new"),
          code: "pending",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["pending"]
            }
          }
        },
        {
          name: t("parts.counters.shortlisting"),
          code: "shortlisting",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["shortlisting"]
            }
          }
        },
        {
          name: t("parts.counters.inReview"),
          code: "reviewing",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["reviewing"]
            }
          }
        },
        {
          name: t("parts.counters.invited"),
          code: "inviting",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["inviting"]
            }
          }
        },
        {
          name: t("parts.counters.partiallyFilled"),
          code: "partiallyFilled",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["partially_filled"]
            }
          }
        },
        {
          name: t("parts.counters.filled"),
          code: "filled",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["filled"]
            }
          },
          separator: !isBookingModuleEnabled
        },
        isBookingModuleEnabled && {
          name: t("parts.counters.partiallyBooked"),
          code: "partiallyBooked",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["partially_booked"]
            }
          }
        },
        isBookingModuleEnabled && {
          name: t("parts.counters.booked"),
          code: "booked",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["booked"]
            }
          }
        },
        isBookingModuleEnabled && {
          name: t("parts.counters.partiallyConfirmed"),
          code: "partiallyConfirmed",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["partially_confirmed"]
            }
          }
        },
        isBookingModuleEnabled && {
          name: t("parts.counters.confirmed"),
          code: "confirmed",
          filters: {
            fields: {
              state: ["new"],
              workflow_state: ["confirmed"]
            }
          },
          separator: true
        },
        {
          name: t("parts.counters.draft"),
          code: "draft",
          filters: {
            fields: {
              state: ["draft"],
              workflow_state: Object.keys(WORKFLOW_STATES)
            }
          }
        },
        {
          name: t("parts.counters.closed"),
          code: "complete",
          filters: {
            fields: {
              state: ["complete"],
              workflow_state: Object.keys(WORKFLOW_STATES)
            }
          }
        }
      ].filter((item) => item),
    [isBookingModuleEnabled, t]
  );

  const activeCounter = useActiveCounter(counters, meta);

  return (
    <div className={classNames(css.container, className)}>
      <div className={css.scrollWrapper}>
        <SearchInput
          term={termValue}
          onTermChange={handleTermChange}
          variant="gray"
          qaId="WorkflowPageRolesSearch"
          className={css.search}
        />
        <div className={css.counters}>
          {counters.map(({ name, code, separator }) => (
            <React.Fragment key={`${code}_${stringify(valueFilters)}`}>
              <Counter
                title={name}
                code={code}
                selected={activeCounter === code}
                counter={fetchedCounters[code]}
                onClick={() => {
                  onFiltersReset({
                    ...valueFilters,
                    fields: {
                      ...valueFilters.fields,
                      ...statusFilters(code).fields
                    }
                  });
                }}
                qaIdPrefix="WorkflowPageRolesCounter"
              />
              {separator && <div className={css.separator} />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

RolesHeader.propTypes = {
  className: PropTypes.string
};

export default RolesHeader;
